import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom, SCOPES, useHasScope } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { AxiosResponse, AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@data/notification';
import { useAtomCallback } from 'jotai/utils';

type PatchBody = {
  settings: {
    enable_application_to_autoware_status?: boolean;
  };
};

/**
 * 車両固有パラメータの設定変更API
 */
export const useCalibrationParameterSettingsAPI = (): {
  state: APIState;
  patchEnableApplicationToAutowareStatus: (
    vehicleId: string,
    value: boolean,
  ) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getHasScope = useHasScope();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      body: PatchBody,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.patch(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/calibration_parameter_settings`,
          body,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const patchEnableApplicationToAutowareStatus = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, value: boolean) => {
        if (!getHasScope(SCOPES.UpdateCalibrationParameterSettings)) {
          setState('hasError');
          return false;
        }
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t(
              'api.fms.patch_calibration_parameter_settings.enable_application_to_autoware_status',
              {
                status: 'failed',
              },
            ),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await request(
          project.id,
          environment.environment_id,
          vehicleId,
          {
            settings: {
              enable_application_to_autoware_status: value,
            },
          },
        );
        if (!res.value || res.isFailure()) {
          notifyError({
            message: `${t(
              'api.fms.patch_calibration_parameter_settings.enable_application_to_autoware_status',
              {
                status: 'failed',
              },
            )}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }
        notifySuccess({
          message: t(
            'api.fms.patch_calibration_parameter_settings.enable_application_to_autoware_status',
            {
              status: 'successfully',
            },
          ),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, request, getHasScope, t, getErrorMessage],
    ),
  );

  return {
    state,
    patchEnableApplicationToAutowareStatus,
  };
};
